import React, { useEffect, useState } from "react";
import partnerService from "../../services/KangarooServices/PartnerService";
import MediaService from "../../services/KangarooServices/MediaServices";
import { useFormik } from "formik";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Input,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import MediaModal from "../PagesSection/Component/MediaModal";
import { imageBaseApi } from "../../utilis/imageHelper";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

const PartnerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mediaData, setMediaData] = useState([]);
  const [details, setDetails] = useState(null);
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [currentDetailIndex, setCurrentDetailIndex] = useState(null); // For which detail we're selecting media
  const [activeTab, setActiveTab] = useState(0);

  // Fetch all media once
  const fetchMedia = async () => {
    try {
      const response = await MediaService.getList({ page: 1, pageSize: 1000 });
      setMediaData(response?.items || []);
    } catch (error) {
      console.error("Failed to fetch media:", error);
      toast.error("Failed to load media library");
    }
  };

  // Fetch partner detail if editing
  const fetchDetail = async () => {
    if (!id) return;
    try {
      const result = await partnerService.getDetail(id);
      setDetails(result);

      // Pre-select existing media
      if (result.partnerDetails?.length > 0) {
        const preselected = result.partnerDetails.map((detail) => ({
          id: detail.logoMediaId,
          url: detail.logoMedia?.url || "",
          mimeType: detail.logoMedia?.mimeType || "",
          fileName: detail.logoMedia?.fileName || "",
        }));
        formik.setFieldValue("selectedMedia", preselected);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch partner details");
    }
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  useEffect(() => {
    if (id) fetchDetail();
  }, [id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: details?.name || "",
      orderIndex: details?.orderIndex || 0,
      partnerDetails:
        details?.partnerDetails?.length > 0
          ? details.partnerDetails.map((d) => ({
              name: d.name || "",
              orderIndex: d.orderIndex || 0,
              logoMediaId: d.logoMediaId || null,
            }))
          : [{ name: "", orderIndex: 0, logoMediaId: null }],
      selectedMedia: [], // Track selected media objects (not just IDs)
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Partner name is required"),
      orderIndex: Yup.number()
        .required("Order Index is required")
        .min(0, "Order Index must be at least 0"),
      partnerDetails: Yup.array()
        .of(
          Yup.object({
            name: Yup.string().required("Detail name is required"),
            orderIndex: Yup.number()
              .required("Order Index is required")
              .min(0, "Order Index must be at least 0"),
            logoMediaId: Yup.string().nullable(),
          })
        )
        .min(1, "At least one partner detail is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          name: values.name,
          orderIndex: values.orderIndex,
          partnerDetails: values.partnerDetails.map((detail, index) => ({
            name: detail.name,
            orderIndex: detail.orderIndex,
            logoMediaId:
              values.selectedMedia[index]?.id || detail.logoMediaId || null,
          })),
        };

        if (id) {
          await partnerService.update(id, payload);
          toast.success("Partner updated successfully!");
        } else {
          await partnerService.create(payload);
          toast.success("Partner created successfully!");
        }
        navigate("/partners");
      } catch (error) {
        console.error("Save error:", error);
        toast.error(error.response?.data?.message || "Failed to save partner");
      } finally {
        setLoading(false);
      }
    },
  });

  // Open media modal for specific detail index
  const openMediaModal = (index) => {
    setCurrentDetailIndex(index);
    setActiveTab(index);
    setIsMediaOpen(true);
  };

  // Handle media selection from modal
  const handleMediaSelect = (media) => {
    if (currentDetailIndex !== null) {
      const newSelected = [...(formik.values.selectedMedia || [])];
      newSelected[currentDetailIndex] = media;

      formik.setFieldValue("selectedMedia", newSelected);
      formik.setFieldValue(
        `partnerDetails[${currentDetailIndex}].logoMediaId`,
        media?.id || null
      );
    }
    setIsMediaOpen(false);
    setCurrentDetailIndex(null);
  };

  // Add new partner detail tab
  const addPartnerDetail = () => {
    formik.setFieldValue("partnerDetails", [
      ...formik.values.partnerDetails,
      { name: "", orderIndex: 0, logoMediaId: null },
    ]);
    formik.setFieldValue("selectedMedia", [
      ...(formik.values.selectedMedia || []),
      null,
    ]);
    setActiveTab(formik.values.partnerDetails.length);
  };

  // Remove partner detail tab
  const removePartnerDetail = (index) => {
    if (formik.values.partnerDetails.length <= 1) {
      toast.warn("At least one partner detail is required");
      return;
    }
    const newDetails = formik.values.partnerDetails.filter(
      (_, i) => i !== index
    );
    const newMedia = (formik.values.selectedMedia || []).filter(
      (_, i) => i !== index
    );
    formik.setFieldValue("partnerDetails", newDetails);
    formik.setFieldValue("selectedMedia", newMedia);
    if (activeTab >= newDetails.length) {
      setActiveTab(newDetails.length - 1);
    }
  };

  const breadcrumbItems = [{ title: "Back to List", link: "/partners" }];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={id ? "Edit Partner" : "Add Partner"}
          pageTitle="Partners"
          breadcrumbItems={breadcrumbItems}
        />

        <form onSubmit={formik.handleSubmit}>
          <div className="d-flex justify-content-end gap-3 mb-4">
            <Button type="submit" color="success" disabled={loading}>
              {loading ? "Saving..." : id ? "Update" : "Create"}
            </Button>
            <Button
              type="button"
              color="danger"
              onClick={() => navigate("/partners")}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  {/* Main Partner Info */}
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">
                          Partner Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="name"
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.name && !!formik.errors.name}
                        />
                        {formik.touched.name && formik.errors.name && (
                          <div className="text-danger mt-1">
                            {formik.errors.name}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="form-label">
                          Order Index <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="number"
                          name="orderIndex"
                          value={formik.values.orderIndex}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            formik.touched.orderIndex &&
                            !!formik.errors.orderIndex
                          }
                        />
                        {formik.touched.orderIndex &&
                          formik.errors.orderIndex && (
                            <div className="text-danger mt-1">
                              {formik.errors.orderIndex}
                            </div>
                          )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Partner Details Tabs */}
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Partner Details</h5>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={addPartnerDetail}
                    >
                      + Add Detail
                    </Button>
                  </div>

                  <Nav tabs className="mb-3">
                    {formik.values.partnerDetails.map((_, index) => (
                      <NavItem key={index}>
                        <NavLink
                          active={activeTab === index}
                          onClick={() => setActiveTab(index)}
                          style={{ cursor: "pointer" }}
                        >
                          Detail {index + 1}
                          {formik.values.partnerDetails.length > 1 && (
                            <i
                              className="mdi mdi-close ms-2"
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                removePartnerDetail(index);
                              }}
                            />
                          )}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>

                  <TabContent activeTab={activeTab}>
                    {formik.values.partnerDetails.map((detail, index) => (
                      <TabPane tabId={index} key={index}>
                        <Row>
                          <Col lg={4}>
                            <Card>
                              <CardBody className="text-center">
                                {formik.values.selectedMedia[index] ? (
                                  <>
                                    {formik.values.selectedMedia[
                                      index
                                    ].mimeType?.startsWith("image") ? (
                                      <img
                                        src={`${imageBaseApi}${formik.values.selectedMedia[index].url}`}
                                        alt="Selected"
                                        className="img-fluid rounded"
                                        style={{ maxHeight: "200px" }}
                                      />
                                    ) : formik.values.selectedMedia[index]
                                        .mimeType === "application/pdf" ? (
                                      <div className="py-5">
                                        <i className="bi bi-file-earmark-pdf-fill fs-1 text-danger"></i>
                                        <p className="mt-2">
                                          {
                                            formik.values.selectedMedia[index]
                                              .fileName
                                          }
                                        </p>
                                      </div>
                                    ) : (
                                      <div className="py-5">
                                        <i className="bi bi-file-earmark fs-1"></i>
                                        <p>
                                          {
                                            formik.values.selectedMedia[index]
                                              .fileName
                                          }
                                        </p>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="py-2 text-muted">
                                    <i className="bi bi-image fs-1"></i>

                                    <p>No media selected</p>
                                  </div>
                                )}
                                <Button
                                  color="primary"
                                  size="sm"
                                  className="mt-3"
                                  onClick={() => openMediaModal(index)}
                                >
                                  {formik.values.selectedMedia[index]
                                    ? "Change"
                                    : "Select"}{" "}
                                  Media
                                </Button>
                              </CardBody>
                            </Card>
                          </Col>

                          <Col lg={8}>
                            <Row>
                              <Col md={12}>
                                <div className="">
                                  <Label>
                                    Name <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    name={`partnerDetails[${index}].name`}
                                    value={detail.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={
                                      formik.touched.partnerDetails?.[index]
                                        ?.name &&
                                      !!formik.errors.partnerDetails?.[index]
                                        ?.name
                                    }
                                  />
                                  {formik.touched.partnerDetails?.[index]
                                    ?.name &&
                                    formik.errors.partnerDetails?.[index]
                                      ?.name && (
                                      <div className="text-danger mt-1">
                                        {
                                          formik.errors.partnerDetails[index]
                                            .name
                                        }
                                      </div>
                                    )}
                                </div>
                              </Col>
                              <Col md={12}>
                                <div className="">
                                  <Label>
                                    Order Index{" "}
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="number"
                                    name={`partnerDetails[${index}].orderIndex`}
                                    value={detail.orderIndex}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={
                                      formik.touched.partnerDetails?.[index]
                                        ?.orderIndex &&
                                      !!formik.errors.partnerDetails?.[index]
                                        ?.orderIndex
                                    }
                                  />
                                  {formik.touched.partnerDetails?.[index]
                                    ?.orderIndex &&
                                    formik.errors.partnerDetails?.[index]
                                      ?.orderIndex && (
                                      <div className="text-danger mt-1">
                                        {
                                          formik.errors.partnerDetails[index]
                                            .orderIndex
                                        }
                                      </div>
                                    )}
                                </div>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </TabPane>
                    ))}
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>

        {/* Media Selection Modal */}
        <MediaModal
          isOpen={isMediaOpen}
          toggle={() => setIsMediaOpen(false)}
          mediaData={mediaData}
          selectedMedia={
            formik.values.selectedMedia[currentDetailIndex] || null
          }
          setSelectedMedia={handleMediaSelect}
        />
      </Container>
    </div>
  );
};

export default PartnerForm;
